import React from 'react';
import { CheckCircleIcon, ExclamationCircleIcon, DocumentIcon, TableCellsIcon, PhotoIcon } from '@heroicons/react/24/outline';
import UploadProgressBar from './UploadProgressBar_Component';
import { formatFileSize } from './utils/formatFileSize';

export interface UploadItem {
  id: string;
  fileName?: string;
  fileSize?: number;
  progress?: number;
  status?: string;
  completed?: boolean;
  error?: boolean;
}

interface UploadTableProps {
  uploads?: UploadItem[];
}

/**
 * Get appropriate icon for file based on extension
 */
const getFileIcon = (fileName?: string): JSX.Element => {
  // Handle missing or invalid fileName
  if (!fileName || typeof fileName !== 'string') {
    return <DocumentIcon className="w-4 h-4 flex-shrink-0 text-gray-600" />;
  }

  const extension = fileName.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return <DocumentIcon className="w-4 h-4 flex-shrink-0 text-red-600" />;
    case 'xlsx':
    case 'xls':
    case 'csv':
      return <TableCellsIcon className="w-4 h-4 flex-shrink-0 text-green-600" />;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
      return <PhotoIcon className="w-4 h-4 flex-shrink-0 text-blue-600" />;
    default:
      return <DocumentIcon className="w-4 h-4 flex-shrink-0 text-gray-600" />;
  }
};

/**
 * Get status display text and color
 */
const getStatusDisplay = (status?: string, completed?: boolean, error?: boolean): { text: string; className: string } => {
  if (error) {
    return { text: 'Failed', className: 'text-red-600' };
  }

  if (completed) {
    return { text: 'Complete', className: 'text-green-600' };
  }

  // Handle specific status strings
  if (status) {
    // Backend processing statuses
    if (status.includes('Processing document') || status === 'Processing') {
      return { text: 'Processing', className: 'text-purple-600' };
    }
    if (status === 'Ready') {
      return { text: 'Ready', className: 'text-green-600' };
    }
    // Upload phase statuses
    if (status.includes('Waiting for processing')) {
      return { text: 'Waiting', className: 'text-gray-600' };
    }
    if (status.includes('Uploading') || status === 'uploading') {
      return { text: 'Uploading', className: 'text-blue-600' };
    }
    if (status.includes('Preparing') || status.includes('Getting upload URL')) {
      return { text: 'Preparing', className: 'text-gray-600' };
    }
  }

  return { text: status || 'Uploading', className: 'text-gray-600' };
};

/**
 * Upload Table Component - displays multiple file uploads in a clean table format
 */
const UploadTable: React.FC<UploadTableProps> = ({ uploads = [] }) => {
  // Don't render if no uploads
  if (!uploads || uploads.length === 0) {
    return null;
  }

  return (
    <div className="w-full bg-white rounded-lg shadow-sm">
      {/* Table Header */}
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-medium text-gray-900">
          Files ({uploads.length})
        </h3>
      </div>

      {/* Table Content */}
      <div className="divide-y divide-gray-50">
        {uploads.map((upload, index) => {
          const { text: statusText, className: statusClass } = getStatusDisplay(
            upload.status,
            upload.completed,
            upload.error
          );

          // Determine upload status for progress bar
          let uploadStatus: 'uploading' | 'processing' | 'completed' | 'error' = 'uploading';
          if (upload.error) uploadStatus = 'error';
          else if (upload.completed) uploadStatus = 'completed';
          else if (statusText === 'Processing' || statusText === 'Waiting') uploadStatus = 'processing';

          return (
            <div
              key={upload.id || index}
              className="px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-4">
                {/* File Icon and Name */}
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {getFileIcon(upload.fileName)}
                  <span className="text-sm text-gray-900 truncate" title={upload.fileName || 'Uploading...'}>
                    {upload.fileName || 'Uploading...'}
                  </span>
                </div>

                {/* File Size */}
                <div className="text-sm text-gray-500 min-w-[60px] text-right">
                  {upload.fileSize ? formatFileSize(upload.fileSize) : '-'}
                </div>

                {/* Progress Bar */}
                <div className="flex-1 max-w-[200px]">
                  <UploadProgressBar
                    progress={upload.progress || 0}
                    status={uploadStatus}
                    showPercentage={false}
                  />
                </div>

                {/* Status with Icon */}
                <div className={`flex items-center gap-1 min-w-[100px] justify-end`}>
                  {upload.completed && !upload.error && (
                    <CheckCircleIcon className="w-4 h-4 flex-shrink-0 text-green-600" />
                  )}
                  {upload.error && (
                    <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0 text-red-600" />
                  )}
                  <span className={`text-sm ${statusClass}`}>
                    {statusText}
                  </span>
                  {uploadStatus === 'uploading' && (upload.progress || 0) > 0 && (
                    <span className="text-sm text-gray-500 ml-1">
                      ({upload.progress}%)
                    </span>
                  )}
                </div>
              </div>
              {/* Error detail message */}
              {upload.error && upload.status && upload.status !== 'error' && (
                <p className="text-xs text-red-500 mt-1 pl-6 truncate" title={upload.status}>
                  {upload.status}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UploadTable;
